import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import resize from 'FileManagerLib/utils/resize';
import getIsFileManagerUrl from 'FileManagerLib/utils/isFileManagerUrl';
import { CALYPSO_MEDIUM } from 'HubStyleTokens/colors';
import { MICRO_TRANSITION_TIMING } from 'HubStyleTokens/times';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIRouterButton from 'ui-addon-react-router-dom/UIRouterButton';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import * as EmbedFrame from 'marketplace-embed/constants/EmbedFrame';
import * as EmbedGroup from 'marketplace-embed/constants/EmbedGroup';
import * as PostMessageType from 'marketplace-embed/constants/PostMessageType';
/* @ts-expect-error module is untyped */

import ThemeCard from 'ecosystem-marketplace-core/components/cards/ThemeCard';

/* @ts-expect-error module is untyped */
import { useEmbedContext } from '../../../hooks/useEmbedContext';
import { getAuth // @ts-expect-error untyped dependency
} from '../../../selectors/auth';
import ThemeDemoButton from '../../details/ThemeDemoButton';
import ThemeListViewCard from './ThemeListViewCard';
const IMAGE_MAX_WIDTH = 480;
const StyledLink = styled(props => /*#__PURE__*/_jsx(UIRouterLink, Object.assign({}, props, {
  use: "unstyled"
}))).withConfig({
  displayName: "RouterThemeCard__StyledLink",
  componentId: "sc-6za5v9-0"
})(["display:flex;align-items:stretch;"]);
StyledLink.displayName = 'StyledLink';
const StyledThemeCard = styled(ThemeCard).withConfig({
  displayName: "RouterThemeCard__StyledThemeCard",
  componentId: "sc-6za5v9-1"
})(["&&{border:2px solid transparent;transition:border ", " ease-out;&:hover{border-color:", ";}}"], MICRO_TRANSITION_TIMING, CALYPSO_MEDIUM);

const RouterThemeCard = ({
  onClick,
  onTrackInteraction,
  onTrackPageview,
  result,
  view
}) => {
  const {
    isEmbedded,
    name: embedFrameName,
    sendMessage,
    sendBroadcastMessage
  } = useEmbedContext();
  const auth = useSelector(getAuth);
  const {
    companyName,
    exampleUrl,
    featureHeadlines,
    installsCount,
    name,
    priceCents,
    reviewSummary,
    slug,
    thumbnailUrl
  } = result;
  const ThemeResultCard = useMemo(() => view === 'list' ? ThemeListViewCard : StyledThemeCard, [view]);
  const getResizedThumbnailUrl = useCallback(() => {
    /* We use a third-party image service for BE Acceptance Tests. Avoid sending
     * these photos to the resizing service to prevent noisy `console.error` logs */
    if (!getIsFileManagerUrl(thumbnailUrl)) return thumbnailUrl;
    return resize(thumbnailUrl, {
      width: IMAGE_MAX_WIDTH
    }, undefined, auth);
  }, [auth, thumbnailUrl]);
  const handleOnClick = useCallback(evt => {
    if (isEmbedded && embedFrameName === EmbedFrame.CONTENT_CREATOR_STOREFRONT) {
      const messageArgs = [PostMessageType.NAVIGATE_TO_THEME_DETAILS, {
        slug
      }];
      sendBroadcastMessage(...messageArgs, EmbedGroup.CONTENT_CREATOR);
      sendMessage(...messageArgs);
      evt.preventDefault();
    }

    if (onClick) {
      onClick(evt);
    }
  }, [embedFrameName, isEmbedded, onClick, sendBroadcastMessage, sendMessage, slug]);

  const detailsButton = /*#__PURE__*/_jsx(UIRouterButton, {
    "data-test-id": "storefront-theme-card",
    "data-theme-slug": slug,
    onClick: handleOnClick,
    to: `/${slug}`,
    use: "primary",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-assets-ui.themes.details.detailsButton"
    })
  });

  const demoButton = /*#__PURE__*/_jsx(ThemeDemoButton, {
    exampleUrl: exampleUrl,
    name: name,
    onTrackInteraction: onTrackInteraction,
    onTrackPageview: onTrackPageview,
    modalPrimaryButton: detailsButton,
    providerName: companyName,
    section: "storefront-theme-card",
    use: "secondary"
  });

  return /*#__PURE__*/_jsx(ThemeResultCard, {
    demoButton: demoButton,
    detailsButton: detailsButton,
    exampleUrl: exampleUrl,
    featureHeadlines: featureHeadlines,
    installsCount: installsCount,
    name: name,
    priceCents: priceCents,
    providerName: companyName,
    reviewSummary: reviewSummary,
    slug: slug,
    thumbnailUrl: getResizedThumbnailUrl()
  });
};

export default RouterThemeCard;